import Phaser from 'phaser';
import { NetworkClient } from './NetworkClient';
import { Sfx } from './Sfx';
import type { CorpseState, PatchDefinition, PlayerState, ShotState, WelcomeMessage } from './types';

const WORLD_WIDTH = 6000;
const WORLD_HEIGHT = 4000;
const NIGHT_DURATION_MS = 90_000;
const PLAYER_SPEED = 245;
const MAX_WALL_LEVEL = 4;
const BULLET_RANGE = 560;
const WALL_BODY_LENGTH = 82;
const WALL_BODY_THICKNESS = 42;
const WALL_HIT_INTERVAL_MS = 3000;
const MONSTER_USER_AGGRO_RADIUS = 1050;
const MONSTER_BOT_AGGRO_RADIUS = 760;
const COLORS = [0xc8ff4d, 0x57e7ff, 0xff8f66, 0xd98cff, 0xffd45d, 0x74f2a7];

interface CorpseVisual {
  state: CorpseState;
  sprite: Phaser.Physics.Arcade.Sprite;
  healthBack: Phaser.GameObjects.Rectangle;
  healthFill: Phaser.GameObjects.Rectangle;
  levelLabel: Phaser.GameObjects.Text;
}

interface BotSurvivor {
  id: string;
  name: string;
  color: number;
  sprite: Phaser.Physics.Arcade.Sprite;
  label: Phaser.GameObjects.Text;
  health: number;
  alive: boolean;
  nextDecisionAt: number;
  nextShotAt: number;
  direction: Phaser.Math.Vector2;
}

interface RemoteSurvivor {
  sprite: Phaser.GameObjects.Sprite;
  label: Phaser.GameObjects.Text;
  targetX: number;
  targetY: number;
  targetRotation: number;
}

interface LivingTarget {
  kind: 'local' | 'remote' | 'bot';
  id: string;
  x: number;
  y: number;
}

const PATCHES: PatchDefinition[] = [
  {
    id: 'rapid',
    title: '긴급 사격 규약',
    description: '사격 간격이 40% 짧아집니다.',
  },
  {
    id: 'heavy',
    title: '중량 탄두',
    description: '탄환 피해가 2배가 되지만 탄속이 느려집니다.',
  },
  {
    id: 'stillness',
    title: '정지 시간',
    description: '당신이 멈추면 몬스터의 시간이 65% 느려집니다.',
  },
  {
    id: 'fortify',
    title: '골편 보강술',
    description: '모든 성벽의 최대 내구도가 25% 증가합니다.',
  },
  {
    id: 'shield',
    title: '기억의 방패',
    description: '주변 시체 하나당 받는 피해가 감소합니다.',
  },
  {
    id: 'dashBlast',
    title: '충격 대시',
    description: '대시할 때 주변 몬스터를 밀치고 피해를 줍니다.',
  },
];

export class GameScene extends Phaser.Scene {
  private readonly playerName: string;
  private readonly sfx: Sfx;
  private network!: NetworkClient;

  private player!: Phaser.Physics.Arcade.Sprite;
  private playerColor = COLORS[0];
  private playerHealth = 100;
  private playerAlive = true;
  private invulnerableUntil = 0;
  private playerId = `offline-${crypto.randomUUID()}`;
  private score = 0;
  private biomass = 3;
  private night = 1;
  private nightStartedAt = 0;
  private nightEnded = false;

  private monsters!: Phaser.Physics.Arcade.Group;
  private bullets!: Phaser.Physics.Arcade.Group;
  private corpseGroup!: Phaser.Physics.Arcade.Group;
  private corpseRecords = new Map<string, CorpseVisual>();
  private builtCorpses = new Set<string>();
  private bots = new Map<string, BotSurvivor>();
  private remotes = new Map<string, RemoteSurvivor>();
  private wallSafePositions = new WeakMap<Phaser.GameObjects.Sprite, Phaser.Math.Vector2>();

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;
  private interactKey!: Phaser.Input.Keyboard.Key;
  private buildKey!: Phaser.Input.Keyboard.Key;
  private reinforceKey!: Phaser.Input.Keyboard.Key;
  private carriedCorpseId: string | null = null;
  private buildMode = false;
  private lastAimAngle = 0;
  private lastShotAt = 0;
  private lastDashAt = -5000;
  private dashUntil = 0;
  private nextMonsterAt = 0;
  private nextNetworkStateAt = 0;
  private nextBombAt = 0;
  private nextBruteAt = 0;
  private interactQueued = false;
  private buildQueued = false;
  private reinforceQueued = false;
  private dashQueued = false;
  private patchSelectionIndex = 0;
  private patchSelecting = false;
  private appliedPatches = new Set<string>();
  private networkWorldPlayers = 1;
  private networkWorldCorpses = 0;

  private readonly healthElement = document.querySelector<HTMLElement>('#health-value')!;
  private readonly timeElement = document.querySelector<HTMLElement>('#time-value')!;
  private readonly corpseElement = document.querySelector<HTMLElement>('#corpse-value')!;
  private readonly wallElement = document.querySelector<HTMLElement>('#wall-value')!;
  private readonly scoreElement = document.querySelector<HTMLElement>('#score-value')!;
  private readonly biomassElement = document.querySelector<HTMLElement>('#biomass-value')!;
  private readonly networkElement = document.querySelector<HTMLElement>('#network-status')!;
  private readonly promptElement = document.querySelector<HTMLElement>('#interaction-prompt')!;
  private readonly feedElement = document.querySelector<HTMLElement>('#event-feed')!;
  private readonly patchPanel = document.querySelector<HTMLElement>('#patch-panel')!;
  private readonly patchOptions = document.querySelector<HTMLElement>('#patch-options')!;
  private readonly endPanel = document.querySelector<HTMLElement>('#end-panel')!;

  constructor(playerName: string, sfx: Sfx) {
    super({ key: 'GameScene' });
    this.playerName = playerName;
    this.sfx = sfx;
  }

  create(): void {
    this.createTextures();
    this.createWorld();
    this.createGroups();
    this.createPlayer();
    this.createBots();
    this.createInput();
    this.createPhysicsRules();
    this.createNetwork();

    this.nightStartedAt = this.time.now;
    this.nextMonsterAt = this.time.now + 700;
    this.nextBombAt = this.time.now + 34_000;
    this.nextBruteAt = this.time.now + 49_000;
    for (let index = 0; index < 6; index += 1) this.spawnMonster(true);
    this.time.delayedCall(900, () => this.ensureTutorialCorpse());

    const continueButton = document.querySelector<HTMLButtonElement>('#continue-button')!;
    continueButton.onclick = () => this.startNextNight();

    this.feed('WORLD', '6,000×4,000 무법 구역. 모든 실제 생존자는 적입니다.');
    this.feed('CONTROL', 'E 운반 · B 성벽 배치 · R 성벽 강화 · 마우스 PvP 사격');
    this.feed('MISSION', '시체로 방벽을 만들고 밤의 공성에서 지켜내세요.');

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.network?.close();
      continueButton.onclick = null;
    });
  }

  update(time: number, delta: number): void {
    if (this.nightEnded || this.patchSelecting) {
      this.updateRemotePlayers();
      this.resolveWorldWallCollisions();
      return;
    }

    if (this.playerAlive) {
      this.updatePlayer(time);
      this.updateCarriedCorpse();
    }
    this.updateBullets(time);
    this.updateMonsters(time, delta);
    this.updateBots(time);
    this.updateRemotePlayers();
    this.resolveWorldWallCollisions();
    this.updateSpawning(time);
    this.updateSiege(time);
    this.updateNight(time);
    this.updatePrompt();
    this.updateHud(time);
    this.updateNetwork(time);
  }

  private createTextures(): void {
    const graphics = this.make.graphics({ x: 0, y: 0 });

    graphics.clear();
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(32, 32, 15);
    graphics.lineStyle(3, 0x0a0d12, 1);
    graphics.strokeCircle(32, 32, 15);
    graphics.fillStyle(0x0a0d12, 1);
    graphics.fillTriangle(45, 27, 61, 32, 45, 37);
    graphics.generateTexture('survivor', 64, 64);

    graphics.clear();
    graphics.fillStyle(0xffffff, 1);
    graphics.fillTriangle(28, 4, 54, 51, 4, 51);
    graphics.lineStyle(3, 0x0a0d12, 1);
    graphics.strokeTriangle(28, 4, 54, 51, 4, 51);
    graphics.fillStyle(0x0a0d12, 1);
    graphics.fillCircle(20, 33, 3);
    graphics.fillCircle(36, 33, 3);
    graphics.generateTexture('monster', 58, 58);

    graphics.clear();
    graphics.fillStyle(0xffffff, 1);
    graphics.fillCircle(6, 6, 5);
    graphics.generateTexture('bullet', 12, 12);

    graphics.clear();
    graphics.fillStyle(0xffffff, 0.95);
    graphics.fillRoundedRect(5, 8, 60, 20, 10);
    graphics.fillCircle(14, 18, 12);
    graphics.lineStyle(2, 0x0a0d12, 0.75);
    graphics.lineBetween(31, 10, 42, 26);
    graphics.lineBetween(42, 10, 31, 26);
    graphics.generateTexture('corpse-human', 70, 36);

    graphics.clear();
    graphics.fillStyle(0xffffff, 0.92);
    graphics.fillTriangle(3, 29, 19, 4, 34, 29);
    graphics.fillTriangle(28, 29, 45, 4, 65, 29);
    graphics.lineStyle(2, 0x0a0d12, 0.65);
    graphics.lineBetween(16, 15, 51, 21);
    graphics.generateTexture('corpse-monster', 68, 34);

    graphics.clear();
    graphics.lineStyle(2, 0xc8ff4d, 0.9);
    graphics.strokeCircle(48, 48, 42);
    graphics.lineStyle(1, 0xc8ff4d, 0.4);
    graphics.strokeCircle(48, 48, 31);
    graphics.generateTexture('pulse', 96, 96);

    graphics.destroy();
  }

  private createWorld(): void {
    this.physics.world.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.cameras.main.setBackgroundColor('#090c10');

    const background = this.add.graphics().setDepth(-20);
    background.fillStyle(0x090c10, 1);
    background.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    background.lineStyle(1, 0x182028, 0.68);
    for (let x = 0; x <= WORLD_WIDTH; x += 80) background.lineBetween(x, 0, x, WORLD_HEIGHT);
    for (let y = 0; y <= WORLD_HEIGHT; y += 80) background.lineBetween(0, y, WORLD_WIDTH, y);
    background.lineStyle(2, 0x243039, 0.5);
    for (let x = 0; x <= WORLD_WIDTH; x += 400) background.lineBetween(x, 0, x, WORLD_HEIGHT);
    for (let y = 0; y <= WORLD_HEIGHT; y += 400) background.lineBetween(0, y, WORLD_WIDTH, y);

    const markings = this.add.graphics().setDepth(-19);
    for (let index = 0; index < 18; index += 1) {
      const x = 150 + ((index * 521) % (WORLD_WIDTH - 300));
      const y = 150 + ((index * 337) % (WORLD_HEIGHT - 300));
      markings.lineStyle(2, index % 3 === 0 ? 0xff6846 : 0x31414a, 0.32);
      markings.strokeCircle(x, y, 45 + (index % 4) * 18);
      markings.lineBetween(x - 65, y, x + 65, y);
    }

    this.add.text(48, 42, 'SECTOR 09 // THE LAST COMMON GROUND', {
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: '18px',
      color: '#26333b',
      letterSpacing: 2,
    }).setDepth(-18);
  }

  private createGroups(): void {
    this.monsters = this.physics.add.group();
    this.bullets = this.physics.add.group();
    this.corpseGroup = this.physics.add.group({
      immovable: true,
      allowGravity: false,
    });
  }

  private createPlayer(): void {
    const spawn = this.randomSpawn();
    this.player = this.physics.add.sprite(spawn.x, spawn.y, 'survivor');
    this.player.setTint(this.playerColor).setDepth(10).setCollideWorldBounds(true);
    this.player.setCircle(15, 17, 17);
    this.player.setMaxVelocity(700);
    this.invulnerableUntil = this.time.now + 4000;
    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
    this.cameras.main.setZoom(1);

    this.add.text(spawn.x, spawn.y - 32, this.playerName, {
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: '12px',
      color: '#c8ff4d',
      backgroundColor: '#090c10aa',
      padding: { x: 5, y: 2 },
    }).setName('local-player-label').setOrigin(0.5).setDepth(11);
  }

  private createBots(): void {
    const crew = [
      ['아샤', COLORS[1]],
      ['메이', COLORS[2]],
      ['루카', COLORS[3]],
      ['산호', COLORS[4]],
      ['노아', COLORS[5]],
    ] as const;

    for (const [index, member] of crew.entries()) {
      const spawn = this.randomSpawn(index * 400);
      const sprite = this.physics.add.sprite(spawn.x, spawn.y, 'survivor');
      sprite.setTint(member[1]).setDepth(8).setCollideWorldBounds(true).setScale(0.88);
      sprite.setCircle(15, 17, 17);
      const label = this.add.text(spawn.x, spawn.y - 29, member[0], {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '11px',
        color: Phaser.Display.Color.IntegerToColor(member[1]).rgba,
        backgroundColor: '#090c10aa',
        padding: { x: 4, y: 1 },
      }).setOrigin(0.5).setDepth(9);
      this.bots.set(`crew-${index}`, {
        id: `crew-${index}`,
        name: member[0],
        color: member[1],
        sprite,
        label,
        health: 75,
        alive: true,
        nextDecisionAt: 0,
        nextShotAt: 0,
        direction: new Phaser.Math.Vector2(1, 0),
      });
    }
  }

  private createInput(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) throw new Error('Keyboard input is unavailable.');
    this.cursors = keyboard.createCursorKeys();
    this.wasd = keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
    }) as Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;
    this.interactKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    this.buildKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.B);
    this.reinforceKey = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.R);
    keyboard.on('keydown-E', (event: KeyboardEvent) => {
      if (!event.repeat) this.interactQueued = true;
    });
    keyboard.on('keydown-B', (event: KeyboardEvent) => {
      if (!event.repeat) this.buildQueued = true;
    });
    keyboard.on('keydown-R', (event: KeyboardEvent) => {
      if (!event.repeat) this.reinforceQueued = true;
    });
    keyboard.on('keydown-SHIFT', (event: KeyboardEvent) => {
      if (!event.repeat) this.dashQueued = true;
    });
  }

  private createPhysicsRules(): void {
    this.physics.world.setFPS(120);
    this.physics.add.collider(this.player, this.corpseGroup);
    this.physics.add.collider(this.monsters, this.corpseGroup, (monster, corpse) => {
      this.onMonsterHitsCorpse(monster as Phaser.Physics.Arcade.Sprite, corpse as Phaser.Physics.Arcade.Sprite);
    });
    for (const bot of this.bots.values()) this.physics.add.collider(bot.sprite, this.corpseGroup);
    this.physics.add.overlap(this.bullets, this.monsters, (bullet, monster) => {
      this.onBulletHitsMonster(bullet as Phaser.Physics.Arcade.Sprite, monster as Phaser.Physics.Arcade.Sprite);
    });
    this.physics.add.collider(this.bullets, this.corpseGroup, (bullet) => {
      (bullet as Phaser.Physics.Arcade.Sprite).destroy();
    });
  }

  private createNetwork(): void {
    this.network = new NetworkClient(this.playerName, {
      onConnected: (connected) => {
        this.networkElement.textContent = connected
          ? `공유 세계 연결됨 · ${this.networkWorldPlayers}명 생존`
          : '오프라인 피난처 · AI 생존자 활성';
        if (connected) this.feed('LINK', '다른 생존자와 시체 기록이 동기화됩니다.');
      },
      onWelcome: (message) => this.onNetworkWelcome(message),
      onPlayerState: (player) => this.onRemotePlayer(player),
      onPlayerLeave: (id) => this.removeRemotePlayer(id),
      onCorpseAdd: (corpse) => this.upsertCorpse(corpse, false),
      onCorpseMove: (corpse) => this.upsertCorpse(corpse, false),
      onCorpseRemove: (id) => this.removeCorpse(id),
      onShot: (shot) => this.spawnShot(shot, false),
      onWorldCount: (players, corpses) => {
        this.networkWorldPlayers = players;
        this.networkWorldCorpses = corpses;
        if (this.network.connected) this.networkElement.textContent = `공유 세계 · ${players}명 · 시체 ${corpses}`;
      },
    });
    this.network.connect();
  }

  private onNetworkWelcome(message: WelcomeMessage): void {
    this.playerId = message.id;
    this.player.setPosition(message.spawn.x, message.spawn.y);
    this.wallSafePositions.delete(this.player);
    for (const player of message.players) {
      if (player.id !== this.playerId) this.onRemotePlayer(player);
    }
    for (const corpse of message.corpses) this.upsertCorpse(corpse, false);
  }

  private updatePlayer(time: number): void {
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const left = this.wasd.left.isDown || this.cursors.left.isDown;
    const right = this.wasd.right.isDown || this.cursors.right.isDown;
    const up = this.wasd.up.isDown || this.cursors.up.isDown;
    const down = this.wasd.down.isDown || this.cursors.down.isDown;
    const movement = new Phaser.Math.Vector2(Number(right) - Number(left), Number(down) - Number(up));

    const dashPressed = this.dashQueued;
    this.dashQueued = false;
    if (dashPressed && time - this.lastDashAt > 1200) {
      const dashDirection = movement.lengthSq() > 0
        ? movement.clone().normalize()
        : new Phaser.Math.Vector2(Math.cos(this.lastAimAngle), Math.sin(this.lastAimAngle));
      body.setVelocity(dashDirection.x * 680, dashDirection.y * 680);
      this.dashUntil = time + 150;
      this.lastDashAt = time;
      this.createPulse(this.player.x, this.player.y, this.playerColor);
      if (this.appliedPatches.has('dashBlast')) this.damageMonstersInRadius(this.player.x, this.player.y, 120, 2);
    } else if (time > this.dashUntil) {
      if (movement.lengthSq() > 0) movement.normalize().scale(PLAYER_SPEED);
      body.setVelocity(movement.x, movement.y);
    }

    const pointer = this.input.activePointer;
    const worldPoint = pointer.positionToCamera(this.cameras.main) as Phaser.Math.Vector2;
    this.lastAimAngle = Phaser.Math.Angle.Between(this.player.x, this.player.y, worldPoint.x, worldPoint.y);
    this.player.setRotation(this.lastAimAngle);

    const fireDelay = this.appliedPatches.has('rapid') ? 105 : 175;
    if (pointer.isDown && time - this.lastShotAt >= fireDelay && !this.carriedCorpseId) {
      this.fireBullet(this.player.x, this.player.y, this.lastAimAngle, this.playerColor, this.playerId, true);
      this.lastShotAt = time;
    }

    if (this.buildQueued) {
      this.buildQueued = false;
      this.buildMode = !this.buildMode;
      this.feed('BUILD', this.buildMode ? '성벽 정렬이 켜졌습니다.' : '자유 배치로 전환했습니다.');
    }
    if (this.interactQueued) {
      this.interactQueued = false;
      if (this.carriedCorpseId) this.dropCarriedCorpse();
      else this.pickUpNearestCorpse();
    }
    if (this.reinforceQueued) {
      this.reinforceQueued = false;
      this.reinforceNearestWall();
    }

    const label = this.children.getByName('local-player-label') as Phaser.GameObjects.Text | null;
    label?.setPosition(this.player.x, this.player.y - 32).setVisible(this.playerAlive);
  }

  private updateCarriedCorpse(): void {
    if (!this.carriedCorpseId) return;
    const visual = this.corpseRecords.get(this.carriedCorpseId);
    if (!visual) {
      this.carriedCorpseId = null;
      return;
    }

    const distance = this.buildMode ? 72 : 52;
    let x = this.player.x + Math.cos(this.lastAimAngle) * distance;
    let y = this.player.y + Math.sin(this.lastAimAngle) * distance;
    let rotation = this.lastAimAngle;
    if (this.buildMode) {
      x = Math.round(x / 32) * 32;
      y = Math.round(y / 32) * 32;
      rotation = Math.round(rotation / (Math.PI / 2)) * (Math.PI / 2);
    }
    visual.sprite.setPosition(x, y).setRotation(rotation);
    visual.state.x = x;
    visual.state.y = y;
    visual.state.rotation = rotation;
    this.updateCorpseVisual(visual);
  }

  private pickUpNearestCorpse(): void {
    let nearest: CorpseVisual | undefined;
    let nearestDistance = 92;
    for (const visual of this.corpseRecords.values()) {
      if (visual.state.carriedBy) continue;
      const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, visual.sprite.x, visual.sprite.y);
      if (distance < nearestDistance) {
        nearest = visual;
        nearestDistance = distance;
      }
    }
    if (!nearest) return;

    this.carriedCorpseId = nearest.state.id;
    nearest.state.carriedBy = this.playerId;
    const body = nearest.sprite.body as Phaser.Physics.Arcade.Body;
    body.enable = false;
    nearest.sprite.setDepth(12).setAlpha(0.92);
    this.updateCorpseVisual(nearest);
    this.sfx.pickup();
    this.network.moveCorpse(nearest.state);
    this.feed('SALVAGE', `${nearest.state.ownerName ?? '이름 없는 존재'}의 흔적을 운반합니다.`);
  }

  private dropCarriedCorpse(): void {
    if (!this.carriedCorpseId) return;
    const visual = this.corpseRecords.get(this.carriedCorpseId);
    if (!visual) return;
    visual.state.carriedBy = null;
    visual.state.x = visual.sprite.x;
    visual.state.y = visual.sprite.y;
    visual.state.rotation = visual.sprite.rotation;
    if (this.buildMode) {
      const firstBuild = !visual.state.isWall;
      visual.state.isWall = true;
      visual.state.wallLevel = Math.max(1, visual.state.wallLevel);
      const baseHealth = visual.state.kind === 'human' ? 125 : 95;
      const fortifyBonus = this.appliedPatches.has('fortify') ? 1.25 : 1;
      visual.state.wallMaxHealth = Math.round((baseHealth + (visual.state.wallLevel - 1) * 65) * fortifyBonus);
      visual.state.wallHealth = firstBuild
        ? visual.state.wallMaxHealth
        : Math.min(visual.state.wallMaxHealth, Math.max(1, visual.state.wallHealth));
    } else {
      visual.state.isWall = false;
      this.builtCorpses.delete(visual.state.id);
    }
    const body = visual.sprite.body as Phaser.Physics.Arcade.Body;
    body.enable = visual.state.isWall;
    body.setVelocity(0, 0).setImmovable(true);
    this.resizeCorpseBody(visual);
    visual.sprite.setDepth(4).setAlpha(1);
    this.updateCorpseVisual(visual);
    this.network.moveCorpse(visual.state);
    this.carriedCorpseId = null;
    const firstBuild = this.buildMode && !this.builtCorpses.has(visual.state.id);
    if (firstBuild) {
      this.builtCorpses.add(visual.state.id);
      this.score += 25;
      this.announceWallMilestone();
    } else if (!this.buildMode) {
      this.score += 8;
    }
    this.sfx.drop();
    this.createPulse(visual.sprite.x, visual.sprite.y, 0xc8ff4d);
    this.feed('BUILD', this.buildMode ? '죽은 시체를 통과 불가 물리 성벽으로 고정했습니다.' : '시체를 세계에 내려놓았습니다.');
  }

  private fireBullet(
    x: number,
    y: number,
    angle: number,
    color: number,
    ownerId: string,
    broadcast = false,
  ): void {
    const shot: ShotState = {
      id: `shot-${crypto.randomUUID()}`,
      ownerId,
      x: x + Math.cos(angle) * 28,
      y: y + Math.sin(angle) * 28,
      angle,
      color,
      damage: this.appliedPatches.has('heavy') ? 2 : 1,
      createdAt: Date.now(),
    };
    this.spawnShot(shot, broadcast);
  }

  private spawnShot(shot: ShotState, broadcast: boolean): void {
    const bullet = this.bullets.create(shot.x, shot.y, 'bullet') as Phaser.Physics.Arcade.Sprite;
    const speed = this.appliedPatches.has('heavy') ? 520 : 760;
    bullet.setTint(shot.color).setDepth(6);
    bullet.setData('bornAt', this.time.now);
    bullet.setData('damage', shot.damage);
    bullet.setData('ownerId', shot.ownerId);
    bullet.setData('originX', shot.x);
    bullet.setData('originY', shot.y);
    bullet.setData('ricochet', 0);
    this.physics.velocityFromRotation(shot.angle, speed, bullet.body!.velocity);
    if (broadcast) this.network.sendShot(shot);
    if (shot.ownerId === this.playerId) this.sfx.shot();
  }

  private updateBullets(time: number): void {
    this.bullets.children.each((child) => {
      const bullet = child as Phaser.Physics.Arcade.Sprite;
      if (!bullet.active) return true;
      const traveled = Phaser.Math.Distance.Squared(
        Number(bullet.getData('originX') ?? bullet.x),
        Number(bullet.getData('originY') ?? bullet.y),
        bullet.x,
        bullet.y,
      );
      if (time - Number(bullet.getData('bornAt')) > 1400
        || traveled >= BULLET_RANGE ** 2
        || bullet.x < -30 || bullet.x > WORLD_WIDTH + 30
        || bullet.y < -30 || bullet.y > WORLD_HEIGHT + 30) {
        bullet.destroy();
        return true;
      }
      const ownerId = String(bullet.getData('ownerId') ?? '');
      if (this.playerAlive && ownerId !== this.playerId
        && Phaser.Math.Distance.Squared(bullet.x, bullet.y, this.player.x, this.player.y) < 25 ** 2) {
        const damage = Number(bullet.getData('damage') ?? 1);
        bullet.destroy();
        this.damagePlayer(damage * 14);
        this.feed('PVP', '적 생존자의 탄환에 맞았습니다.');
        return true;
      }
      for (const bot of this.bots.values()) {
        if (!bullet.active || !bot.alive || ownerId === bot.id) continue;
        if (Phaser.Math.Distance.Squared(bullet.x, bullet.y, bot.sprite.x, bot.sprite.y) < 23 ** 2) {
          const damage = Number(bullet.getData('damage') ?? 1);
          bullet.destroy();
          this.damageBot(bot.id, damage * 16);
          break;
        }
      }
      return true;
    });
  }

  private onBulletHitsMonster(
    bullet: Phaser.Physics.Arcade.Sprite,
    monster: Phaser.Physics.Arcade.Sprite,
  ): void {
    if (!bullet.active || !monster.active) return;
    const damage = Number(bullet.getData('damage') ?? 1);
    bullet.destroy();
    this.damageMonster(monster, damage);
  }

  private damageMonster(monster: Phaser.Physics.Arcade.Sprite, damage: number): void {
    if (!monster.active) return;
    const hp = Number(monster.getData('hp') ?? 3) - damage;
    monster.setData('hp', hp);
    monster.setTintFill(0xffffff);
    this.time.delayedCall(55, () => {
      if (monster.active) monster.clearTint().setTint(Number(monster.getData('color') ?? 0xff6846));
    });
    if (hp <= 0) this.killMonster(monster);
  }

  private killMonster(monster: Phaser.Physics.Arcade.Sprite): void {
    if (!monster.active) return;
    const x = monster.x;
    const y = monster.y;
    const color = Number(monster.getData('color') ?? 0xff6846);
    const brute = Boolean(monster.getData('brute'));
    monster.destroy();
    this.score += brute ? 60 : 10;
    this.biomass += brute ? 4 : 1;
    this.sfx.hit();
    this.spawnCorpse('monster', x, y, color, brute ? '공성 거수' : '오염체');
  }

  private spawnMonster(initial = false, brute = false): void {
    if (this.monsters.countActive(true) >= 48) return;
    let x = Phaser.Math.Between(60, WORLD_WIDTH - 60);
    let y = Phaser.Math.Between(60, WORLD_HEIGHT - 60);
    for (let attempt = 0; attempt < 12; attempt += 1) {
      if (initial) {
        x = Phaser.Math.Between(60, WORLD_WIDTH - 60);
        y = Phaser.Math.Between(60, WORLD_HEIGHT - 60);
      } else {
        const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);
        const distance = Phaser.Math.Between(540, 760);
        x = Phaser.Math.Clamp(this.player.x + Math.cos(angle) * distance, 60, WORLD_WIDTH - 60);
        y = Phaser.Math.Clamp(this.player.y + Math.sin(angle) * distance, 60, WORLD_HEIGHT - 60);
      }
      const overlapsWall = [...this.corpseRecords.values()].some((visual) =>
        visual.state.isWall && !visual.state.carriedBy
        && Phaser.Math.Distance.Squared(x, y, visual.sprite.x, visual.sprite.y) < 130 ** 2,
      );
      if (!overlapsWall) break;
    }
    const monster = this.monsters.create(x, y, 'monster') as Phaser.Physics.Arcade.Sprite;
    const color = Phaser.Math.RND.pick([0xff6846, 0xff3f61, 0xc962ff, 0xff9f43]);
    monster.setTint(brute ? 0xff274f : color).setDepth(7).setScale(brute ? 1.85 : Phaser.Math.FloatBetween(0.82, 1.16));
    monster.setCircle(brute ? 24 : 22, brute ? 5 : 7, brute ? 5 : 7).setCollideWorldBounds(true);
    monster.setData('hp', brute ? 22 + this.night * 5 : Phaser.Math.Between(2, 4));
    monster.setData('speed', brute ? 52 + this.night * 3 : Phaser.Math.Between(66, 105));
    monster.setData('color', color);
    monster.setData('brute', brute);
    monster.setData('wallDamage', brute ? 28 + this.night * 6 : 10 + this.night * 2);
    monster.setData('nextLocalHit', 0);
    monster.setData('targetKind', '');
    monster.setData('targetId', '');
    monster.setData('nextTargetAt', 0);
    monster.setData('nextSteerAt', 0);
    monster.setData('steerX', 0);
    monster.setData('steerY', 0);
    monster.setData('avoidanceSide', Phaser.Math.RND.pick([-1, 1]));
    if (brute) this.feed('SIEGE', `공성 거수가 출현했습니다. 성벽 충돌 피해 ${22 + this.night * 5}.`);
  }

  private updateMonsters(time: number, _delta: number): void {
    const localStationary = this.playerAlive
      && Math.abs(this.player.body!.velocity.x) + Math.abs(this.player.body!.velocity.y) < 4;
    const timeFactor = this.appliedPatches.has('stillness') && localStationary ? 0.35 : 1;

    this.monsters.children.each((child) => {
      const monster = child as Phaser.Physics.Arcade.Sprite;
      if (!monster.active) return true;
      let target = this.getLockedMonsterTarget(monster);
      const targetDistance = target
        ? Phaser.Math.Distance.Between(monster.x, monster.y, target.x, target.y)
        : Number.POSITIVE_INFINITY;
      if (!target || targetDistance > MONSTER_USER_AGGRO_RADIUS * 1.2
        || time >= Number(monster.getData('nextTargetAt') ?? 0)) {
        target = this.findClosestLivingTarget(monster.x, monster.y);
        monster.setData('nextTargetAt', time + 360);
        monster.setData('targetKind', target?.kind ?? '');
        monster.setData('targetId', target?.id ?? '');
      }
      if (!target) {
        monster.setVelocity(0, 0);
        return true;
      }
      let angle = Phaser.Math.Angle.Between(monster.x, monster.y, target.x, target.y);
      if (!Boolean(monster.getData('brute'))) {
        if (time >= Number(monster.getData('nextSteerAt') ?? 0)) {
          monster.setData('nextSteerAt', time + 130);
          const steering = this.calculateWallAvoidance(monster, target.x, target.y);
          monster.setData('steerX', steering.x);
          monster.setData('steerY', steering.y);
        }
        const steerX = Number(monster.getData('steerX') ?? 0);
        const steerY = Number(monster.getData('steerY') ?? 0);
        if (Math.abs(steerX) + Math.abs(steerY) > 0.01) angle = Math.atan2(steerY, steerX);
      }
      const speed = Number(monster.getData('speed')) * timeFactor;
      monster.setVelocity(Math.cos(angle) * speed, Math.sin(angle) * speed).setRotation(angle + Math.PI / 2);

      if (target.kind === 'local' && Phaser.Math.Distance.Between(monster.x, monster.y, target.x, target.y) < 35) {
        if (time >= Number(monster.getData('nextLocalHit'))) {
          monster.setData('nextLocalHit', time + 620);
          this.damagePlayer(6);
        }
      } else if (target.kind === 'bot' && Phaser.Math.Distance.Between(monster.x, monster.y, target.x, target.y) < 34) {
        const key = `nextHit-${target.id}`;
        if (time >= Number(monster.getData(key) ?? 0)) {
          monster.setData(key, time + 720);
          this.damageBot(target.id, 9);
        }
      }
      return true;
    });
  }

  private getLockedMonsterTarget(monster: Phaser.Physics.Arcade.Sprite): LivingTarget | null {
    const kind = String(monster.getData('targetKind') ?? '');
    const id = String(monster.getData('targetId') ?? '');
    if (kind === 'local' && this.playerAlive) {
      return { kind: 'local', id: this.playerId, x: this.player.x, y: this.player.y };
    }
    if (kind === 'remote') {
      const remote = this.remotes.get(id);
      if (remote?.sprite.visible) return { kind: 'remote', id, x: remote.sprite.x, y: remote.sprite.y };
    }
    if (kind === 'bot') {
      const bot = this.bots.get(id);
      if (bot?.alive) return { kind: 'bot', id, x: bot.sprite.x, y: bot.sprite.y };
    }
    return null;
  }

  private findClosestLivingTarget(x: number, y: number): LivingTarget | null {
    let result: LivingTarget | null = null;
    let distance = MONSTER_USER_AGGRO_RADIUS ** 2;
    if (this.playerAlive) {
      const localDistance = Phaser.Math.Distance.Squared(x, y, this.player.x, this.player.y);
      if (localDistance <= distance) {
        result = { kind: 'local', id: this.playerId, x: this.player.x, y: this.player.y };
        distance = localDistance;
      }
    }
    for (const [id, remote] of this.remotes) {
      if (!remote.sprite.visible) continue;
      const nextDistance = Phaser.Math.Distance.Squared(x, y, remote.sprite.x, remote.sprite.y);
      if (nextDistance <= distance) {
        result = { kind: 'remote', id, x: remote.sprite.x, y: remote.sprite.y };
        distance = nextDistance;
      }
    }
    if (result) return result;

    distance = MONSTER_BOT_AGGRO_RADIUS ** 2;
    for (const bot of this.bots.values()) {
      if (!bot.alive) continue;
      const nextDistance = Phaser.Math.Distance.Squared(x, y, bot.sprite.x, bot.sprite.y);
      if (nextDistance < distance) {
        result = { kind: 'bot', id: bot.id, x: bot.sprite.x, y: bot.sprite.y };
        distance = nextDistance;
      }
    }
    return result;
  }

  private onMonsterHitsCorpse(
    monster: Phaser.Physics.Arcade.Sprite,
    corpse: Phaser.Physics.Arcade.Sprite,
  ): void {
    const id = String(corpse.getData('corpseId') ?? '');
    const visual = this.corpseRecords.get(id);
    if (!visual?.state.isWall) return;
    monster.setData('nextSteerAt', 0);
    this.attackWall(monster, visual);
  }

  private attackWall(monster: Phaser.Physics.Arcade.Sprite, visual: CorpseVisual): void {
    if (!visual.state.isWall || !monster.active) return;
    const cooldownKey = `wall-hit-${visual.state.id}`;
    if (this.time.now < Number(monster.getData(cooldownKey) ?? 0)) return;
    monster.setData(cooldownKey, this.time.now + WALL_HIT_INTERVAL_MS);
    this.damageWall(visual, Number(monster.getData('wallDamage') ?? 10));
    this.createPulse(visual.sprite.x, visual.sprite.y, 0xff6846);
  }

  private calculateWallAvoidance(
    monster: Phaser.Physics.Arcade.Sprite,
    targetX: number,
    targetY: number,
  ): Phaser.Math.Vector2 {
    const desired = new Phaser.Math.Vector2(targetX - monster.x, targetY - monster.y).normalize();
    let closest: CorpseVisual | undefined;
    let closestDistance = 260;
    const targetDistance = Phaser.Math.Distance.Between(monster.x, monster.y, targetX, targetY);
    const monsterRadius = this.wallRadiusFor(monster, Boolean(monster.getData('brute')) ? 48 : 24);
    for (const visual of this.corpseRecords.values()) {
      if (!visual.state.isWall || visual.state.carriedBy) continue;
      const offset = new Phaser.Math.Vector2(visual.sprite.x - monster.x, visual.sprite.y - monster.y);
      const distance = offset.length();
      const alongPath = offset.dot(desired);
      const acrossPath = Math.abs(offset.x * desired.y - offset.y * desired.x);
      const wallBody = visual.sprite.body as Phaser.Physics.Arcade.Body;
      const clearance = Math.max(wallBody.width, wallBody.height) * 0.5 + monsterRadius;
      if (distance >= closestDistance || alongPath <= 0 || alongPath >= targetDistance
        || alongPath > 260 || acrossPath > clearance) continue;
      closest = visual;
      closestDistance = distance;
    }
    if (!closest) return desired;
    const away = new Phaser.Math.Vector2(monster.x - closest.sprite.x, monster.y - closest.sprite.y).normalize();
    const wallDirection = new Phaser.Math.Vector2(
      Math.cos(closest.sprite.rotation),
      Math.sin(closest.sprite.rotation),
    );
    const targetSide = wallDirection.dot(desired);
    const savedSide = Number(monster.getData('avoidanceSide') ?? 1);
    const side = Math.abs(targetSide) > 0.12 ? Math.sign(targetSide) : savedSide;
    const tangent = wallDirection.scale(side);
    const pressure = Phaser.Math.Clamp((260 - closestDistance) / 180, 0.25, 1);
    return desired
      .scale(0.32)
      .add(away.scale(0.8 + pressure * 0.55))
      .add(tangent.scale(1.1 + pressure * 0.65))
      .normalize();
  }

  private resolveWorldWallCollisions(): void {
    if (this.playerAlive) this.resolveSpriteAgainstWalls(this.player, this.wallRadiusFor(this.player, 24));

    for (const bot of this.bots.values()) {
      if (!bot.alive) continue;
      this.resolveSpriteAgainstWalls(bot.sprite, this.wallRadiusFor(bot.sprite, 22));
      bot.label.setPosition(bot.sprite.x, bot.sprite.y - 29);
    }

    this.monsters.children.each((child) => {
      const monster = child as Phaser.Physics.Arcade.Sprite;
      if (!monster.active) return true;
      const hitWall = this.resolveSpriteAgainstWalls(
        monster,
        this.wallRadiusFor(monster, Boolean(monster.getData('brute')) ? 48 : 24),
      );
      if (hitWall) this.attackWall(monster, hitWall);
      return true;
    });

    for (const remote of this.remotes.values()) {
      this.resolveSpriteAgainstWalls(remote.sprite, this.wallRadiusFor(remote.sprite, 24));
      remote.label.setPosition(remote.sprite.x, remote.sprite.y - 32);
    }
  }

  private wallRadiusFor(sprite: Phaser.GameObjects.Sprite, minimum: number): number {
    return Math.max(minimum, Math.max(sprite.displayWidth, sprite.displayHeight) * 0.5);
  }

  private resolveSpriteAgainstWalls(
    sprite: Phaser.GameObjects.Sprite,
    radius: number,
  ): CorpseVisual | null {
    const previous = this.wallSafePositions.get(sprite);
    let x = sprite.x;
    let y = sprite.y;
    let hitWall: CorpseVisual | null = null;
    let hitNormalX = 0;
    let hitNormalY = 0;

    if (previous && (previous.x !== x || previous.y !== y)) {
      let earliestTime = Number.POSITIVE_INFINITY;
      for (const visual of this.corpseRecords.values()) {
        if (!visual.state.isWall || visual.state.carriedBy) continue;
        const wallBody = visual.sprite.body as Phaser.Physics.Arcade.Body;
        if (!wallBody.enable) continue;
        const sweep = this.sweepPointAgainstWall(previous.x, previous.y, x, y, wallBody, radius);
        if (!sweep || sweep.time >= earliestTime) continue;
        earliestTime = sweep.time;
        hitWall = visual;
        hitNormalX = sweep.normalX;
        hitNormalY = sweep.normalY;
      }

      if (hitWall) {
        const distance = Phaser.Math.Distance.Between(previous.x, previous.y, x, y);
        const safeTime = Math.max(0, earliestTime - 0.9 / Math.max(distance, 0.9));
        x = Phaser.Math.Linear(previous.x, x, safeTime);
        y = Phaser.Math.Linear(previous.y, y, safeTime);
      }
    }

    for (const visual of this.corpseRecords.values()) {
      if (!visual.state.isWall || visual.state.carriedBy) continue;
      const wallBody = visual.sprite.body as Phaser.Physics.Arcade.Body;
      if (!wallBody.enable) continue;

      const nearestX = Phaser.Math.Clamp(x, wallBody.left, wallBody.right);
      const nearestY = Phaser.Math.Clamp(y, wallBody.top, wallBody.bottom);
      const deltaX = x - nearestX;
      const deltaY = y - nearestY;
      const distanceSquared = deltaX * deltaX + deltaY * deltaY;
      if (distanceSquared >= radius * radius) continue;

      hitWall = visual;
      if (distanceSquared > 0.0001) {
        const distance = Math.sqrt(distanceSquared);
        hitNormalX = deltaX / distance;
        hitNormalY = deltaY / distance;
        const penetration = radius - distance + 0.75;
        x += hitNormalX * penetration;
        y += hitNormalY * penetration;
      } else {
        const exits = [
          { distance: Math.abs(x - wallBody.left), x: wallBody.left - radius - 0.75, y, nx: -1, ny: 0 },
          { distance: Math.abs(wallBody.right - x), x: wallBody.right + radius + 0.75, y, nx: 1, ny: 0 },
          { distance: Math.abs(y - wallBody.top), x, y: wallBody.top - radius - 0.75, nx: 0, ny: -1 },
          { distance: Math.abs(wallBody.bottom - y), x, y: wallBody.bottom + radius + 0.75, nx: 0, ny: 1 },
        ].sort((a, b) => a.distance - b.distance);
        const exit = exits[0];
        x = exit.x;
        y = exit.y;
        hitNormalX = exit.nx;
        hitNormalY = exit.ny;
      }
    }

    if (hitWall) {
      sprite.setPosition(x, y);
      const physicsBody = (sprite as Phaser.Physics.Arcade.Sprite).body as Phaser.Physics.Arcade.Body | null;
      if (physicsBody) {
        physicsBody.updateFromGameObject();
        const inwardSpeed = physicsBody.velocity.x * hitNormalX + physicsBody.velocity.y * hitNormalY;
        if (inwardSpeed < 0) {
          physicsBody.velocity.x -= hitNormalX * inwardSpeed;
          physicsBody.velocity.y -= hitNormalY * inwardSpeed;
        }
      }
    }
    this.wallSafePositions.set(sprite, new Phaser.Math.Vector2(sprite.x, sprite.y));
    return hitWall;
  }

  private sweepPointAgainstWall(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    wallBody: Phaser.Physics.Arcade.Body,
    radius: number,
  ): { time: number; normalX: number; normalY: number } | null {
    const left = wallBody.left - radius;
    const right = wallBody.right + radius;
    const top = wallBody.top - radius;
    const bottom = wallBody.bottom + radius;
    if (startX > left && startX < right && startY > top && startY < bottom) return null;

    const deltaX = endX - startX;
    const deltaY = endY - startY;
    let entryTime = Number.NEGATIVE_INFINITY;
    let exitTime = Number.POSITIVE_INFINITY;
    let normalX = 0;
    let normalY = 0;

    const axes = [
      { start: startX, delta: deltaX, minimum: left, maximum: right, minNormalX: -1, minNormalY: 0, maxNormalX: 1, maxNormalY: 0 },
      { start: startY, delta: deltaY, minimum: top, maximum: bottom, minNormalX: 0, minNormalY: -1, maxNormalX: 0, maxNormalY: 1 },
    ];

    for (const axis of axes) {
      if (Math.abs(axis.delta) < 0.0001) {
        if (axis.start < axis.minimum || axis.start > axis.maximum) return null;
        continue;
      }
      const towardMinimum = axis.delta > 0;
      const nearTime = ((towardMinimum ? axis.minimum : axis.maximum) - axis.start) / axis.delta;
      const farTime = ((towardMinimum ? axis.maximum : axis.minimum) - axis.start) / axis.delta;
      if (nearTime > entryTime) {
        entryTime = nearTime;
        normalX = towardMinimum ? axis.minNormalX : axis.maxNormalX;
        normalY = towardMinimum ? axis.minNormalY : axis.maxNormalY;
      }
      exitTime = Math.min(exitTime, farTime);
      if (entryTime > exitTime) return null;
    }

    if (entryTime < 0 || entryTime > 1 || exitTime < 0) return null;
    return { time: entryTime, normalX, normalY };
  }

  private damageMonstersInRadius(x: number, y: number, radius: number, damage: number): void {
    this.monsters.children.each((child) => {
      const monster = child as Phaser.Physics.Arcade.Sprite;
      const distance = Phaser.Math.Distance.Between(x, y, monster.x, monster.y);
      if (monster.active && distance <= radius) {
        const angle = Phaser.Math.Angle.Between(x, y, monster.x, monster.y);
        monster.setVelocity(Math.cos(angle) * 440, Math.sin(angle) * 440);
        this.damageMonster(monster, damage);
      }
      return true;
    });
  }

  private damagePlayer(amount: number): void {
    if (!this.playerAlive) return;
    if (this.time.now < this.invulnerableUntil) return;
    let adjusted = amount;
    if (this.appliedPatches.has('shield')) {
      let nearby = 0;
      for (const visual of this.corpseRecords.values()) {
        if (!visual.state.carriedBy
          && Phaser.Math.Distance.Squared(this.player.x, this.player.y, visual.sprite.x, visual.sprite.y) < 155 ** 2) {
          nearby += 1;
        }
      }
      adjusted *= Math.max(0.38, 1 - Math.min(nearby, 5) * 0.12);
    }
    this.playerHealth = Math.max(0, this.playerHealth - adjusted);
    this.cameras.main.shake(90, 0.004);
    this.player.setTintFill(0xffffff);
    this.time.delayedCall(70, () => {
      if (this.playerAlive) this.player.clearTint().setTint(this.playerColor);
    });
    if (this.playerHealth <= 0) this.killPlayer();
  }

  private killPlayer(): void {
    if (!this.playerAlive) return;
    this.playerAlive = false;
    this.player.setVisible(false).setVelocity(0, 0);
    (this.player.body as Phaser.Physics.Arcade.Body).enable = false;
    if (this.carriedCorpseId) this.dropCarriedCorpse();
    this.spawnCorpse('human', this.player.x, this.player.y, this.playerColor, this.playerName);
    this.sfx.death();
    this.feed('DEATH', `${this.playerName} 사망. 시체는 세계에 영구 보존됩니다.`);
    this.showPrompt('3초 후 다른 장소에서 다시 태어납니다');
    this.time.delayedCall(3000, () => this.respawnPlayer());
  }

  private respawnPlayer(): void {
    const spawn = this.randomSpawn();
    this.player.setPosition(spawn.x, spawn.y).setVisible(true).setAlpha(0);
    this.wallSafePositions.delete(this.player);
    (this.player.body as Phaser.Physics.Arcade.Body).enable = true;
    this.playerHealth = 100;
    this.playerAlive = true;
    this.invulnerableUntil = this.time.now + 3000;
    this.tweens.add({ targets: this.player, alpha: 1, duration: 380 });
    this.createPulse(spawn.x, spawn.y, this.playerColor);
    this.feed('SPAWN', `${this.playerName}이(가) 다른 구역에서 다시 태어났습니다.`);
  }

  private updateBots(time: number): void {
    for (const bot of this.bots.values()) {
      if (!bot.alive) continue;
      if (time >= bot.nextDecisionAt) {
        bot.nextDecisionAt = time + Phaser.Math.Between(850, 1700);
        const nearest = this.findNearestMonster(bot.sprite.x, bot.sprite.y);
        if (nearest && Phaser.Math.Distance.Between(bot.sprite.x, bot.sprite.y, nearest.x, nearest.y) < 330) {
          const away = new Phaser.Math.Vector2(bot.sprite.x - nearest.x, bot.sprite.y - nearest.y).normalize();
          bot.direction = away.rotate(Phaser.Math.FloatBetween(-0.65, 0.65));
        } else {
          bot.direction = Phaser.Math.RandomXY(new Phaser.Math.Vector2(), 1);
        }
      }
      bot.sprite.setVelocity(bot.direction.x * 105, bot.direction.y * 105);
      if (bot.sprite.x < 80 || bot.sprite.x > WORLD_WIDTH - 80) bot.direction.x *= -1;
      if (bot.sprite.y < 80 || bot.sprite.y > WORLD_HEIGHT - 80) bot.direction.y *= -1;
      bot.sprite.setRotation(bot.direction.angle());
      bot.label.setPosition(bot.sprite.x, bot.sprite.y - 29);

      const target = this.findNearestMonster(bot.sprite.x, bot.sprite.y);
      const playerDistance = Phaser.Math.Distance.Between(bot.sprite.x, bot.sprite.y, this.player.x, this.player.y);
      const attackPlayer = this.playerAlive && playerDistance < 520
        && (!target || playerDistance < Phaser.Math.Distance.Between(bot.sprite.x, bot.sprite.y, target.x, target.y));
      const shotTarget = attackPlayer ? this.player : target;
      if (shotTarget && time >= bot.nextShotAt
        && Phaser.Math.Distance.Between(bot.sprite.x, bot.sprite.y, shotTarget.x, shotTarget.y) < 520) {
        bot.nextShotAt = time + Phaser.Math.Between(580, 900);
        const angle = Phaser.Math.Angle.Between(bot.sprite.x, bot.sprite.y, shotTarget.x, shotTarget.y);
        this.fireBullet(bot.sprite.x, bot.sprite.y, angle, bot.color, bot.id);
      }
    }
  }

  private findNearestMonster(x: number, y: number): Phaser.Physics.Arcade.Sprite | null {
    let result: Phaser.Physics.Arcade.Sprite | null = null;
    let distance = Number.POSITIVE_INFINITY;
    this.monsters.children.each((child) => {
      const monster = child as Phaser.Physics.Arcade.Sprite;
      const nextDistance = Phaser.Math.Distance.Squared(x, y, monster.x, monster.y);
      if (monster.active && nextDistance < distance) {
        result = monster;
        distance = nextDistance;
      }
      return true;
    });
    return result;
  }

  private damageBot(id: string, amount: number): void {
    const bot = this.bots.get(id);
    if (!bot?.alive) return;
    bot.health -= amount;
    bot.sprite.setTintFill(0xffffff);
    this.time.delayedCall(60, () => {
      if (bot.alive) bot.sprite.clearTint().setTint(bot.color);
    });
    if (bot.health > 0) return;

    bot.alive = false;
    bot.sprite.setVisible(false).setVelocity(0, 0);
    bot.label.setVisible(false);
    (bot.sprite.body as Phaser.Physics.Arcade.Body).enable = false;
    this.spawnCorpse('human', bot.sprite.x, bot.sprite.y, bot.color, bot.name);
    this.feed('DEATH', `${bot.name} 사망. 시체가 공유 지형으로 남았습니다.`);
    this.time.delayedCall(Phaser.Math.Between(3500, 5200), () => this.respawnBot(bot));
  }

  private respawnBot(bot: BotSurvivor): void {
    const spawn = this.randomSpawn();
    bot.sprite.setPosition(spawn.x, spawn.y).setVisible(true);
    this.wallSafePositions.delete(bot.sprite);
    bot.label.setVisible(true);
    (bot.sprite.body as Phaser.Physics.Arcade.Body).enable = true;
    bot.health = 75;
    bot.alive = true;
    bot.direction = Phaser.Math.RandomXY(new Phaser.Math.Vector2(), 1);
    this.createPulse(spawn.x, spawn.y, bot.color);
    this.feed('SPAWN', `${bot.name}이(가) ${Math.round(spawn.x)}, ${Math.round(spawn.y)} 구역에서 재탄생했습니다.`);
  }

  private spawnCorpse(kind: 'human' | 'monster', x: number, y: number, color: number, ownerName: string): void {
    const state: CorpseState = {
      id: `corpse-${crypto.randomUUID()}`,
      kind,
      x,
      y,
      rotation: Phaser.Math.FloatBetween(-Math.PI, Math.PI),
      color,
      ownerName,
      carriedBy: null,
      createdAt: Date.now(),
      isWall: false,
      wallLevel: 0,
      wallHealth: 0,
      wallMaxHealth: 0,
    };
    this.upsertCorpse(state, true);
  }

  private upsertCorpse(state: CorpseState, broadcast: boolean): void {
    const existing = this.corpseRecords.get(state.id);
    if (existing) {
      existing.state = { ...state };
      if (state.isWall) this.builtCorpses.add(state.id);
      else this.builtCorpses.delete(state.id);
      if (state.carriedBy !== this.playerId) {
        existing.sprite.setPosition(state.x, state.y).setRotation(state.rotation);
        const body = existing.sprite.body as Phaser.Physics.Arcade.Body;
        body.enable = state.isWall && !state.carriedBy;
        existing.sprite.setAlpha(state.carriedBy ? 0.72 : 1).setDepth(state.carriedBy ? 12 : 4);
        if (!state.carriedBy) this.resizeCorpseBody(existing);
      }
      this.updateCorpseVisual(existing);
      return;
    }

    const texture = state.kind === 'human' ? 'corpse-human' : 'corpse-monster';
    const sprite = this.physics.add.sprite(state.x, state.y, texture);
    sprite.setTint(state.color).setRotation(state.rotation).setDepth(4).setImmovable(true).setPushable(false);
    sprite.setData('corpseId', state.id);
    const healthBack = this.add.rectangle(state.x, state.y - 31, 58, 6, 0x05070a, 0.9).setDepth(14);
    const healthFill = this.add.rectangle(state.x - 28, state.y - 31, 56, 4, 0xc8ff4d, 1).setOrigin(0, 0.5).setDepth(15);
    const levelLabel = this.add.text(state.x, state.y - 42, '', {
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: '9px',
      color: '#f4f7f1',
      backgroundColor: '#090c10bb',
      padding: { x: 3, y: 1 },
    }).setOrigin(0.5).setDepth(15);
    const visual: CorpseVisual = { state: { ...state }, sprite, healthBack, healthFill, levelLabel };
    this.corpseRecords.set(state.id, visual);
    if (state.isWall) this.builtCorpses.add(state.id);
    this.corpseGroup.add(sprite);
    const body = sprite.body as Phaser.Physics.Arcade.Body;
    body.allowGravity = false;
    body.enable = state.isWall && !state.carriedBy;
    this.resizeCorpseBody(visual);
    this.updateCorpseVisual(visual);
    if (broadcast) this.network?.addCorpse(state);
  }

  private resizeCorpseBody(visual: CorpseVisual): void {
    const body = visual.sprite.body as Phaser.Physics.Arcade.Body;
    const vertical = Math.abs(Math.sin(visual.sprite.rotation)) > 0.7;
    body.setSize(
      vertical ? WALL_BODY_THICKNESS : WALL_BODY_LENGTH,
      vertical ? WALL_BODY_LENGTH : WALL_BODY_THICKNESS,
      true,
    );
    body.updateFromGameObject();
  }

  private updateCorpseVisual(visual: CorpseVisual): void {
    const isWall = visual.state.isWall && !visual.state.carriedBy;
    const isCarried = Boolean(visual.state.carriedBy);
    const body = visual.sprite.body as Phaser.Physics.Arcade.Body;
    const healthRatio = visual.state.wallMaxHealth > 0
      ? Phaser.Math.Clamp(visual.state.wallHealth / visual.state.wallMaxHealth, 0, 1)
      : 0;
    visual.sprite
      .setScale(isWall ? 1.08 + visual.state.wallLevel * 0.035 : 1)
      .setDepth(isCarried ? 13 : isWall ? 12 : 4);
    body.enable = isWall;
    body.setImmovable(true);
    body.moves = false;
    body.allowGravity = false;
    if (isWall) this.resizeCorpseBody(visual);
    visual.healthBack.setPosition(visual.sprite.x, visual.sprite.y - 34).setVisible(isWall);
    visual.healthFill
      .setPosition(visual.sprite.x - 28, visual.sprite.y - 34)
      .setDisplaySize(Math.max(1, 56 * healthRatio), 4)
      .setFillStyle(healthRatio > 0.55 ? 0xc8ff4d : healthRatio > 0.25 ? 0xffd45d : 0xff4d5f)
      .setVisible(isWall);
    visual.levelLabel
      .setPosition(visual.sprite.x, visual.sprite.y - 46)
      .setText(`시체 성벽 L${visual.state.wallLevel}`)
      .setVisible(isWall);
  }

  private reinforceNearestWall(): void {
    let nearest: CorpseVisual | undefined;
    let nearestDistance = 112;
    for (const visual of this.corpseRecords.values()) {
      if (!visual.state.isWall || visual.state.carriedBy) continue;
      const distance = Phaser.Math.Distance.Between(this.player.x, this.player.y, visual.sprite.x, visual.sprite.y);
      if (distance < nearestDistance) {
        nearest = visual;
        nearestDistance = distance;
      }
    }
    if (!nearest) {
      this.feed('FORTIFY', '강화할 성벽 가까이에서 R을 누르세요.');
      return;
    }
    if (nearest.state.wallLevel >= MAX_WALL_LEVEL) {
      this.feed('FORTIFY', '이 성벽은 이미 최고 단계입니다.');
      return;
    }
    const cost = nearest.state.wallLevel + 1;
    if (this.biomass < cost) {
      this.feed('FORTIFY', `생체 잔해 ${cost}개가 필요합니다. 현재 ${this.biomass}개.`);
      return;
    }
    this.biomass -= cost;
    nearest.state.wallLevel += 1;
    const baseHealth = nearest.state.kind === 'human' ? 125 : 95;
    const fortifyBonus = this.appliedPatches.has('fortify') ? 1.25 : 1;
    const nextMax = Math.round((baseHealth + (nearest.state.wallLevel - 1) * 65) * fortifyBonus);
    nearest.state.wallHealth = Math.min(nextMax, nearest.state.wallHealth + (nextMax - nearest.state.wallMaxHealth) + 45);
    nearest.state.wallMaxHealth = nextMax;
    this.updateCorpseVisual(nearest);
    this.network.moveCorpse(nearest.state);
    this.createPulse(nearest.sprite.x, nearest.sprite.y, 0x57e7ff);
    this.feed('FORTIFY', `성벽 L${nearest.state.wallLevel} 강화 완료 · 내구도 ${Math.ceil(nearest.state.wallHealth)}`);
  }

  private applyFortifyPatch(): void {
    for (const visual of this.corpseRecords.values()) {
      if (!visual.state.isWall) continue;
      const previousMax = visual.state.wallMaxHealth;
      visual.state.wallMaxHealth = Math.round(previousMax * 1.25);
      visual.state.wallHealth = Math.min(
        visual.state.wallMaxHealth,
        visual.state.wallHealth + visual.state.wallMaxHealth - previousMax,
      );
      this.updateCorpseVisual(visual);
      this.network.moveCorpse(visual.state);
    }
  }

  private damageWall(visual: CorpseVisual, amount: number): void {
    if (!visual.state.isWall) return;
    if (this.network.connected) {
      this.network.damageWall(visual.state.id, amount);
      return;
    }
    visual.state.wallHealth = Math.max(0, visual.state.wallHealth - amount);
    if (visual.state.wallHealth <= 0) this.removeCorpse(visual.state.id);
    else this.updateCorpseVisual(visual);
  }

  private removeCorpse(id: string): void {
    const visual = this.corpseRecords.get(id);
    if (!visual) return;
    if (this.carriedCorpseId === id) this.carriedCorpseId = null;
    visual.sprite.destroy();
    visual.healthBack.destroy();
    visual.healthFill.destroy();
    visual.levelLabel.destroy();
    this.corpseRecords.delete(id);
    this.builtCorpses.delete(id);
    this.feed('BREACH', '시체 성벽이 파괴되어 세계에서 소실되었습니다.');
  }

  private onRemotePlayer(player: PlayerState): void {
    let remote = this.remotes.get(player.id);
    if (!remote) {
      const sprite = this.add.sprite(player.x, player.y, 'survivor').setTint(player.color).setDepth(8).setAlpha(0.82);
      const label = this.add.text(player.x, player.y - 32, player.name, {
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: '11px',
        color: '#ff6846',
        backgroundColor: '#090c10aa',
        padding: { x: 4, y: 1 },
      }).setOrigin(0.5).setDepth(9);
      remote = { sprite, label, targetX: player.x, targetY: player.y, targetRotation: player.rotation };
      this.remotes.set(player.id, remote);
      this.feed('ENEMY', `${player.name}이(가) 적대 생존자로 감지되었습니다.`);
    }
    remote.targetX = player.x;
    remote.targetY = player.y;
    remote.targetRotation = player.rotation;
    remote.sprite.setVisible(player.alive).setTint(player.color);
    remote.label.setVisible(player.alive).setText(`ENEMY · ${player.name}`);
  }

  private updateRemotePlayers(): void {
    for (const remote of this.remotes.values()) {
      const deltaX = remote.targetX - remote.sprite.x;
      const deltaY = remote.targetY - remote.sprite.y;
      const distance = Math.hypot(deltaX, deltaY);
      const interpolation = distance > 0 ? Math.min(0.24, 16 / distance) : 0;
      remote.sprite.x += deltaX * interpolation;
      remote.sprite.y += deltaY * interpolation;
      remote.sprite.rotation = Phaser.Math.Angle.RotateTo(remote.sprite.rotation, remote.targetRotation, 0.18);
      remote.label.setPosition(remote.sprite.x, remote.sprite.y - 32);
    }
  }

  private removeRemotePlayer(id: string): void {
    const remote = this.remotes.get(id);
    if (!remote) return;
    remote.sprite.destroy();
    remote.label.destroy();
    this.remotes.delete(id);
  }

  private updateSpawning(time: number): void {
    if (time < this.nextMonsterAt) return;
    const elapsed = time - this.nightStartedAt;
    const delay = Math.max(420, 1220 - elapsed / 145 - (this.night - 1) * 90);
    this.nextMonsterAt = time + delay;
    this.spawnMonster();
  }

  private updateSiege(time: number): void {
    if (time >= this.nextBruteAt) {
      this.spawnMonster(false, true);
      this.nextBruteAt = time + Math.max(16_000, 36_000 - this.night * 3_000);
    }
    if (time < this.nextBombAt) return;
    this.nextBombAt = time + Math.max(12_000, 26_000 - this.night * 2_200);
    const walls = [...this.corpseRecords.values()].filter((visual) => visual.state.isWall && !visual.state.carriedBy);
    const target = walls.length > 0 ? Phaser.Utils.Array.GetRandom(walls).sprite : this.player;
    const x = Phaser.Math.Clamp(target.x + Phaser.Math.Between(-90, 90), 100, WORLD_WIDTH - 100);
    const y = Phaser.Math.Clamp(target.y + Phaser.Math.Between(-90, 90), 100, WORLD_HEIGHT - 100);
    this.warnBombardment(x, y);
  }

  private warnBombardment(x: number, y: number): void {
    const radius = 145 + this.night * 7;
    const marker = this.add.circle(x, y, radius, 0xff274f, 0.07)
      .setStrokeStyle(4, 0xff274f, 0.85)
      .setDepth(16);
    const warning = this.add.text(x, y, 'ORBITAL STRIKE\n2.8 SEC', {
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: '13px',
      align: 'center',
      color: '#ff6b7f',
      backgroundColor: '#090c10dd',
      padding: { x: 8, y: 5 },
    }).setOrigin(0.5).setDepth(17);
    this.tweens.add({ targets: marker, alpha: 0.2, scale: 0.82, duration: 380, yoyo: true, repeat: 5 });
    this.feed('BOMB', '궤도 폭격 예고. 붉은 원 안의 성벽이 곧 무너집니다.');
    this.time.delayedCall(2800, () => {
      marker.destroy();
      warning.destroy();
      this.explodeBombardment(x, y, radius);
    });
  }

  private explodeBombardment(x: number, y: number, radius: number): void {
    const blast = this.add.circle(x, y, 20, 0xffd45d, 0.82).setDepth(18);
    this.tweens.add({
      targets: blast,
      scale: radius / 18,
      alpha: 0,
      duration: 520,
      ease: 'Quad.Out',
      onComplete: () => blast.destroy(),
    });
    for (const visual of [...this.corpseRecords.values()]) {
      if (visual.state.isWall
        && Phaser.Math.Distance.Squared(x, y, visual.sprite.x, visual.sprite.y) <= radius ** 2) {
        this.damageWall(visual, 38 + this.night * 9);
      }
    }
    if (this.playerAlive && Phaser.Math.Distance.Squared(x, y, this.player.x, this.player.y) <= radius ** 2) {
      this.damagePlayer(24 + this.night * 3);
    }
    this.damageMonstersInRadius(x, y, radius, 5 + this.night);
    this.cameras.main.shake(280, 0.012);
    this.feed('IMPACT', `밤 ${this.night} 폭격 충돌 · 성벽 피해 ${38 + this.night * 9}`);
  }

  private updateNight(time: number): void {
    const elapsed = time - this.nightStartedAt;
    const patchTimes = [25_000, 55_000];
    if (this.patchSelectionIndex < patchTimes.length && elapsed >= patchTimes[this.patchSelectionIndex]) {
      this.patchSelectionIndex += 1;
      this.showPatchSelection();
      return;
    }
    if (elapsed >= NIGHT_DURATION_MS) this.finishNight();
  }

  private showPatchSelection(): void {
    const available = PATCHES.filter((patch) => !this.appliedPatches.has(patch.id));
    if (available.length === 0) return;
    Phaser.Utils.Array.Shuffle(available);
    const choices = available.slice(0, Math.min(3, available.length));
    this.patchSelecting = true;
    this.physics.pause();
    this.patchOptions.replaceChildren();

    for (const [index, patch] of choices.entries()) {
      const button = document.createElement('button');
      button.className = 'patch-option';
      const code = document.createElement('span');
      code.textContent = `PATCH 0${index + 1}`;
      const title = document.createElement('strong');
      title.textContent = patch.title;
      const description = document.createElement('small');
      description.textContent = patch.description;
      button.append(code, title, description);
      button.addEventListener('click', () => {
        this.appliedPatches.add(patch.id);
        if (patch.id === 'fortify') this.applyFortifyPatch();
        this.patchPanel.classList.remove('visible');
        this.patchSelecting = false;
        this.physics.resume();
        this.sfx.patch();
        this.feed('PATCH', `${patch.title} 규칙이 세계에 영구 적용됐습니다.`);
      }, { once: true });
      this.patchOptions.append(button);
    }
    this.patchPanel.classList.add('visible');
  }

  private finishNight(): void {
    if (this.nightEnded) return;
    this.nightEnded = true;
    this.physics.pause();
    document.querySelector<HTMLElement>('#final-score')!.textContent = String(this.score);
    document.querySelector<HTMLElement>('#final-corpses')!.textContent = String(this.corpseRecords.size);
    document.querySelector<HTMLElement>('#final-walls')!.textContent = String(this.builtCorpses.size);
    this.endPanel.querySelector('h2')!.textContent = `${this.night}번째 밤을 버텼습니다`;
    this.endPanel.classList.add('visible');
  }

  private startNextNight(): void {
    this.night += 1;
    this.nightStartedAt = this.time.now;
    this.patchSelectionIndex = 0;
    this.nightEnded = false;
    this.nextMonsterAt = this.time.now + 450;
    this.nextBombAt = this.time.now + Math.max(14_000, 31_000 - this.night * 2_000);
    this.nextBruteAt = this.time.now + Math.max(17_000, 43_000 - this.night * 3_000);
    this.endPanel.classList.remove('visible');
    this.physics.resume();
    this.feed('DAWN', `시체 ${this.corpseRecords.size}구가 남은 채 ${this.night}번째 밤이 시작됩니다.`);
  }

  private updatePrompt(): void {
    if (!this.playerAlive) return;
    if (this.carriedCorpseId) {
      this.showPrompt(this.buildMode ? '[E] 성벽으로 배치 · [B] 자유 배치' : '[E] 내려놓기 · [B] 성벽 정렬');
      return;
    }
    const nearbyWall = [...this.corpseRecords.values()].find((visual) =>
      visual.state.isWall && !visual.state.carriedBy
      && Phaser.Math.Distance.Squared(this.player.x, this.player.y, visual.sprite.x, visual.sprite.y) < 112 ** 2,
    );
    if (nearbyWall) {
      const cost = nearbyWall.state.wallLevel + 1;
      this.showPrompt(`[R] 성벽 L${nearbyWall.state.wallLevel} 강화 (${cost} 잔해) · [E] 이동`);
      return;
    }
    const nearby = [...this.corpseRecords.values()].some((visual) =>
      !visual.state.carriedBy
      && Phaser.Math.Distance.Squared(this.player.x, this.player.y, visual.sprite.x, visual.sprite.y) < 92 ** 2,
    );
    if (nearby) this.showPrompt('[E] 시체 들기 · 옮겨서 아지트 성벽을 만드세요');
    else if (this.buildMode) this.showPrompt('성벽 정렬 ON · 시체에 가까이 가서 [E]');
    else this.promptElement.classList.add('hidden');
  }

  private showPrompt(message: string): void {
    this.promptElement.textContent = message;
    this.promptElement.classList.remove('hidden');
  }

  private updateHud(time: number): void {
    this.healthElement.textContent = String(Math.ceil(this.playerHealth));
    this.healthElement.style.color = this.playerHealth <= 30 ? '#ff6846' : '#f4f7f1';
    const remaining = Math.max(0, NIGHT_DURATION_MS - (time - this.nightStartedAt));
    const totalSeconds = Math.ceil(remaining / 1000);
    this.timeElement.textContent = `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(totalSeconds % 60).padStart(2, '0')}`;
    this.corpseElement.textContent = String(Math.max(this.corpseRecords.size, this.networkWorldCorpses));
    this.wallElement.textContent = String(this.builtCorpses.size);
    this.scoreElement.textContent = String(this.score);
    this.biomassElement.textContent = String(this.biomass);
  }

  private updateNetwork(time: number): void {
    if (time < this.nextNetworkStateAt) return;
    this.nextNetworkStateAt = time + 70;
    this.network.sendPlayerState({
      x: this.player.x,
      y: this.player.y,
      rotation: this.player.rotation,
      color: this.playerColor,
      health: this.playerHealth,
      alive: this.playerAlive,
      carriedCorpseId: this.carriedCorpseId,
    });
    if (this.carriedCorpseId) {
      const corpse = this.corpseRecords.get(this.carriedCorpseId);
      if (corpse) this.network.moveCorpse(corpse.state);
    }
  }

  private createPulse(x: number, y: number, color: number): void {
    const pulse = this.add.image(x, y, 'pulse').setTint(color).setDepth(13).setScale(0.35).setAlpha(0.9);
    this.tweens.add({
      targets: pulse,
      scale: 1.55,
      alpha: 0,
      duration: 420,
      ease: 'Cubic.Out',
      onComplete: () => pulse.destroy(),
    });
  }

  private ensureTutorialCorpse(): void {
    const hasNearbyCorpse = [...this.corpseRecords.values()].some((visual) =>
      Phaser.Math.Distance.Squared(this.player.x, this.player.y, visual.sprite.x, visual.sprite.y) < 145 ** 2,
    );
    if (hasNearbyCorpse) return;
    this.spawnCorpse(
      'monster',
      Phaser.Math.Clamp(this.player.x + 68, 40, WORLD_WIDTH - 40),
      Phaser.Math.Clamp(this.player.y + 26, 40, WORLD_HEIGHT - 40),
      0xff8f46,
      '첫 번째 오염체',
    );
    this.feed('TUTORIAL', '가까운 시체를 E로 들어 성벽의 첫 조각을 놓으세요.');
  }

  private announceWallMilestone(): void {
    const count = this.builtCorpses.size;
    const milestone = count === 5 ? '첫 번째 방벽 완성' : count === 10 ? '아지트 외곽선 완성' : count === 20 ? '요새 등급 달성' : null;
    if (!milestone) return;
    this.score += count * 10;
    this.feed('HAVEN', `${milestone}. 이 흔적은 다음 밤에도 남습니다.`);
    this.createPulse(this.player.x, this.player.y, 0xc8ff4d);
  }

  private randomSpawn(salt = 0): { x: number; y: number } {
    return {
      x: 180 + ((Phaser.Math.Between(0, WORLD_WIDTH - 360) + salt * 191) % (WORLD_WIDTH - 360)),
      y: 180 + ((Phaser.Math.Between(0, WORLD_HEIGHT - 360) + salt * 127) % (WORLD_HEIGHT - 360)),
    };
  }

  private feed(channel: string, message: string): void {
    const entry = document.createElement('div');
    entry.className = 'feed-entry';
    const tag = document.createElement('strong');
    tag.textContent = `[${channel}] `;
    entry.append(tag, document.createTextNode(message));
    this.feedElement.prepend(entry);
    while (this.feedElement.children.length > 5) this.feedElement.lastElementChild?.remove();
    window.setTimeout(() => entry.remove(), 7200);
  }
}
